import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, Input, input, InputSignal, OnInit } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NgbPopover, NgbTooltip, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy } from '@ngneat/until-destroy';
import { I18NextPipe, I18NextService } from 'angular-i18next';
import { CheckboxComponent, NumberComponent, TableCellDirective, TableGridComponent, TableHeader } from 'mbs-ui-kit';
import { ExtendedTableRow } from 'mbs-ui-kit/table-grid/table/table.component';
import { getTableHeaders } from 'mbs-ui-shared@components/buy-license-modal/components/licenses-prices-table/utils/table';
import { LicenseTypePrice, PriceDetails, PriceRange } from 'mbs-ui-shared@components/buy-license-modal/models/calculator';
import { LicensesPipesModule } from 'mbs-ui-shared@components/licenses/pipes/licenses-pipes.module';
import { HelpLinkDirective } from 'mbs-ui-shared@help/helpLink.directive';
import { LicenseType } from 'mbs-ui-shared@models';
import { UtilsModule } from 'mbs-ui-shared@utils/utils.module';

@UntilDestroy()
@Component({
  selector: 'mbs-licenses-prices-table',
  templateUrl: './licenses-prices-table.component.html',
  styleUrls: ['./licenses-prices-table.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    LicensesPipesModule,
    UtilsModule,
    ReactiveFormsModule,
    NgbPopover,
    NgbTooltip,
    HelpLinkDirective,
    NgbTooltipModule,
    I18NextPipe,
    NumberComponent,
    TableGridComponent,
    TableCellDirective,
    CheckboxComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LicensesPricesTableComponent implements OnInit {
  isDiscountedPriceShown = input(true);
  isBuyLicensesAllow = input(false);
  @Input() public licensesCountForm: FormGroup;

  licenseTypes: InputSignal<Array<LicenseTypePrice>> = input();
  isMonthlyPayment = input(true);
  paymentPeriod = computed(() => (this.isMonthlyPayment() ? 'month' : 'year'));
  loading = input(false);

  // Single (checkbox) licenses are provider-wide: once owned they can't be bought again, so hide their row.
  public visibleLicenseTypes = computed(() =>
    (this.licenseTypes() ?? []).filter((license) => !(license.isOnlyOne && license.existCount > 0))
  );

  i18n = inject(I18NextService);

  public headers: TableHeader[];

  public isSalesRangeNA(ranges: Array<PriceRange>) {
    const firstRange = ranges?.[0];
    const isOnlyRange = ranges?.length === 1;
    return isOnlyRange && firstRange?.to == null && firstRange?.percent == 0;
  }

  public trackBy(item: ExtendedTableRow) {
    return item;
  }

  public isOnlyOncePayment(licenseType: LicenseType) {
    return licenseType === LicenseType.Onboarding;
  }

  public getPeriod(licenseType: LicenseType) {
    if (this.isOnlyOncePayment(licenseType)) {
      return null;
    }
    return this.paymentPeriod();
  }

  public getPrices(license: LicenseTypePrice): PriceDetails {
    return this.isMonthlyPayment() ? license.monthlyPrices : license.annualPrices;
  }

  public getRanges(license: LicenseTypePrice): Array<PriceRange> {
    return this.isMonthlyPayment() ? license.monthlyDiscountRanges : license.annualDiscountRanges;
  }

  public getTooltip(licenseType: LicenseType) {
    const texts = {
      [LicenseType.BareMetal]: 'Includes File-level and Image-based backup',
      [LicenseType.Ultimate]: 'Includes File-level, Image-based, MS SQL Server and MS Exchange backup',
      [LicenseType.VirtualMachineSocket]:
        'The VM Server Socket license is required for virtual environments with more than 2 sockets. One license covers one extra socket. This license is an additional one to the VM Server license',
      [LicenseType.VirtualMachine]: 'It contains 2 sockets',
      [LicenseType.RemoteDesktop]:
        'This license allows remote desktop connections to managed computers from Management Console using Connect. The license allows unlimited concurrent connections for every administrator who have it',
      [LicenseType.RMM]:
        'This license is required to enable the monitoring of computer parameters and using the available management tools (i.e. Regedit or PowerShell), and Connect. One license can be assigned to one Administrator and allows an unlimited amount of concurrent connections and monitored hosts',
      [LicenseType.Onboarding]:
        'Please refer to <a target="_blank" rel="noopener noreferrer" href="https://www.msp360.com/download/msp360-professional-services.pdf">MSP360 Managed Backup Onboarding Guide</a> for more information',
      [LicenseType.PremiumSupport]:
        'Please refer to <a target="_blank" rel="noopener noreferrer" href="https://www.msp360.com/download/MSP360-customer-support-reference-guide.pdf">MSP360 Customer Support Reference Guide</a> for more information'
    };
    const tooltipText = texts[licenseType];
    return tooltipText
      ? {
          text: tooltipText,
          interactive: licenseType === LicenseType.Onboarding || licenseType === LicenseType.PremiumSupport
        }
      : null;
  }

  ngOnInit() {
    this.headers = getTableHeaders(this.i18n, this.isBuyLicensesAllow(), this.isDiscountedPriceShown());
  }
}
